import React, { useEffect, useState } from 'react';
import ArrowBackIosIcon from '@mui/icons-material/ArrowBackIos';
import ArrowForwardIosIcon from '@mui/icons-material/ArrowForwardIos';
import { Box, Chip, IconButton, Tooltip } from '@mui/material';
import { styled } from '@mui/material/styles';
import { alpha } from '@mui/material/styles';
import { useInView } from 'react-intersection-observer';

const ListItem = styled('li')(({ theme }) => ({
  margin: theme.spacing(0.5),
  scrollSnapAlign: 'start',
}));

const ScrollButton = styled(Box)(({ theme }) => ({
  position: 'absolute',
  display: 'flex',
  flexDirection: 'row',
  justifyContent: 'center',
  height: '100%',
}));

interface ScrollableChipsProps {
  filterLabels?:
    | {
        label: string;
        value: string;
      }[]
    | null;
  selectedFilter: string;
  setSelectedFilter: (value: string) => void;
}

const ScrollableChips: React.FC<ScrollableChipsProps> = ({
  filterLabels,
  selectedFilter,
  setSelectedFilter,
}) => {
  const scroller = React.useRef<HTMLElement>();

  const [previousButtonHidden, setPreviousButtonHidden] = useState<boolean>(false);
  const [nextButtonHidden, setNextButtonHidden] = useState<boolean>(false);

  const { ref: first, inView: firstInView } = useInView();
  const { ref: last, inView: lastInView } = useInView();

  useEffect(() => {
    firstInView ? setPreviousButtonHidden(true) : setPreviousButtonHidden(false);
    lastInView ? setNextButtonHidden(true) : setNextButtonHidden(false);
  }, [firstInView, lastInView]);

  const scrollPrevious = () => {
    if (scroller.current) {
      const width = scroller.current.clientWidth / 2;
      scroller.current.scrollBy(-width, 0);
    }
  };

  const scrollNext = () => {
    if (scroller.current) {
      const width = scroller.current.clientWidth / 2;
      scroller.current.scrollBy(width, 0);
    }
  };

  return (
    <>
      <Box
        sx={{
          position: 'relative',
        }}
      >
        <Box
          sx={{
            display: 'flex',
            alignItems: 'center',
            listStyle: 'none',
            p: 0,
            m: 0,
            scrollSnapType: 'x mandatory',
            overflowX: 'hidden',
            overflowY: 'hidden',
            overflow: 'hidden',
          }}
          component='ul'
          ref={scroller}
        >
          <ListItem ref={first}></ListItem>
          {filterLabels?.map((filter) => (
            <ListItem key={filter.value}>
              <Chip
                key={filter.value}
                label={filter.label}
                clickable
                onClick={() => setSelectedFilter(filter.value)}
                sx={{
                  height: 40,
                  paddingX: 2,
                  borderRadius: '8px',
                  bgcolor:
                    selectedFilter === filter.value
                      ? (theme) => theme.palette.primary.main + '33' // 20% opac  ity of primary color
                      : (theme) => theme.palette.background.default,
                }}
              />
            </ListItem>
          ))}
          <ListItem ref={last}></ListItem>
        </Box>
        {/* Previous button */}
        {!previousButtonHidden && (
          <ScrollButton
            sx={{
              top: 0,
              left: 0,
              '&:after': {
                height: '100%',
                width: '50px',
                content: '""',
                pointerEvents: 'none',
                background: (theme) =>
                  `linear-gradient(to right, ${theme.palette.background.default} 20%, ${alpha(
                    theme.palette.background.default,
                    0,
                  )} 80%)`,
                zIndex: 100,
              },
            }}
          >
            <Box
              sx={{
                background: (theme) => theme.palette.background.default,
              }}
            >
              <Tooltip title='Previous'>
                <IconButton onClick={scrollPrevious}>
                  <ArrowBackIosIcon />
                </IconButton>
              </Tooltip>
            </Box>
          </ScrollButton>
        )}
        {/* Next Button */}
        {!nextButtonHidden && (
          <ScrollButton
            sx={{
              top: 0,
              right: 0,
              '&:before': {
                height: '100%',
                width: '50px',
                content: '""',
                pointerEvents: 'none',
                background: (theme) =>
                  `linear-gradient(to left, ${theme.palette.background.default} 20%,${alpha(
                    theme.palette.background.default,
                    0,
                  )} 80%)`,
              },
              zIndex: 100,
            }}
          >
            <Box
              sx={{
                background: (theme) => theme.palette.background.default,
              }}
            >
              <Tooltip title='Next'>
                <IconButton onClick={scrollNext}>
                  <ArrowForwardIosIcon />
                </IconButton>
              </Tooltip>
            </Box>
          </ScrollButton>
        )}
      </Box>
    </>
  );
};

export default ScrollableChips;
